/**
 * RouteAppointmentsTab - Route & Appointments Tab (v9.0)
 *
 * Two-column layout for all location nodes:
 * - Left column: Contact info (name, contact person, phone, email, address)
 * - Right column: Appointment info (editable fields based on location type)
 * - Different card types for Port, Warehouse, Yard
 */

import React from 'react';
import { Select } from 'antd';
import {
  AlertTriangle,
  Truck,
  Warehouse,
  Ship,
  MapPin,
  CheckCircle,
  AlertCircle,
  Phone,
  ChevronRight,
  User,
  Mail,
  Building2,
  Navigation,
  RefreshCw,
  ShieldAlert,
  Ban,
  Pencil,
  CalendarClock,
} from 'lucide-react';
import { ContainerTask, RouteNode } from '../../../types/container';
import type { Appointment, AppointmentStatus as ApptStatus, UpdateAppointmentInput, StatusTransitionsMap } from '../../../types/appointment';
import { DriverCard } from '../../shared/DriverCard';
import { Button } from '../../ui/button';
import { generateRouteNodes, isLiveload3NodeTask } from '../../../utils/routeGenerator';
import appointmentApi from '../../../services/appointmentApi';
import { FixPlateButton, type FixPlateState } from '../FixPlateButton';
import { GateFeeButton } from '../GateFeeButton';
import { daysUntilLfd, formatLfdMedium, lfdDateOnly } from '../../../utils/lfdDate';

interface Driver {
  id: string
  name: string
  phone: string
  plateLicense: string
  company: string
  avatar: string
  tasks: Array<{
    id: string
    name: string
    status: 'pending' | 'in-progress' | 'completed'
  }>
  userId?: number
}

interface RouteAppointmentsTabProps {
  container: ContainerTask
  onMakePortAppointment: (nodeIndex: number) => void
  onMakeWarehouseAppointment: () => void
  onReschedulePortAppointment?: (nodeIndex: number) => void
  onRescheduleWarehouseAppointment?: () => void
  // B3 — fired when operator clicks the truck-plate icon on an
  // appointment card. Owner is responsible for invoking the extension's
  // plate-fix flow (typically via useFixPlateFlow hook).
  onFixPlate?: (appointmentId: number) => void
  /** Gate Fee — pays this container's terminal gate fee on eModal. */
  onGateFee?: () => void
  // Fired when operator clicks the calendar-clock icon. Owner kicks off
  // the eModal reschedule flow via useRescheduleFlow hook.
  onReschedule?: (appointmentId: number) => void
  onReroute?: () => void
  onStoreToYard?: () => void
  onCancelStoreToYard?: () => void
  /** 提前把下一程放出来，不等这一程跑完 —— 调度今天要排明天的活 */
  onReleaseNextLeg?: () => void
  onSendToPCC?: () => void
  onContainerChange?: (updates: Partial<ContainerTask>) => void
  driver?: Driver
  allContainerTasks?: ContainerTask[]
  /** Appointments from the new appointments table (optional — falls back to task flat fields) */
  appointments?: Appointment[]
  /** Callback to update an appointment (status change, notes, etc.) */
  onAppointmentUpdate?: (appointmentId: number, data: UpdateAppointmentInput) => Promise<void>
}

// Helpers
const getDaysUntilLFD = (lfd?: string): number | null => daysUntilLfd(lfd)

const getLFDUrgency = (d: number | null) => d === null ? null : d <= 0 ? 'critical' : d <= 2 ? 'warning' : 'normal'

const formatDate = (s?: string) => {
  if (!s || s === 'Pending') return ''
  try {
    return new Date(s).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  } catch { return '' }
}

const formatTime = (s?: string) => {
  if (!s || s === 'Pending') return ''
  try {
    return new Date(s).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  } catch { return '' }
}

const formatTimeRange = (start?: string, end?: string) => {
  const s = formatTime(start), e = formatTime(end)
  return s ? (e ? `${s} - ${e}` : s) : ''
}

const LOCATION_ICONS: Record<string, React.ElementType> = {
  port: Ship, warehouse: Warehouse, yard: MapPin, pcc: AlertTriangle, shipper: MapPin
}

const COLORS: Record<string, { bg: string; ring: string }> = {
  port: { bg: 'bg-blue-500', ring: 'ring-blue-200' },
  warehouse: { bg: 'bg-orange-500', ring: 'ring-orange-200' },
  yard: { bg: 'bg-purple-500', ring: 'ring-purple-200' },
  pcc: { bg: 'bg-amber-500', ring: 'ring-amber-200' },
  shipper: { bg: 'bg-green-500', ring: 'ring-green-200' }
}


// Contact Info Component - displays name, contact person, phone, email, address
const ContactInfo: React.FC<{
  name?: string | null
  contactPerson?: string | null
  phone?: string | null
  email?: string | null
  address?: string | null
  onEmailClick?: (email: string) => void
}> = ({ name, contactPerson, phone, email, address, onEmailClick }) => {
  const hasAnyData = name || contactPerson || phone || email || address

  if (!hasAnyData) {
    return (
      <p className="text-sm text-gray-400 italic">No contact info on file</p>
    )
  }

  return (
    <div className="space-y-2">
      {name && (
        <div className="flex items-start gap-2">
          <Building2 className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-900">{name}</span>
        </div>
      )}
      {contactPerson && (
        <div className="flex items-start gap-2">
          <User className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-gray-600">{contactPerson}</span>
        </div>
      )}
      {phone && (
        <div className="flex items-start gap-2">
          <Phone className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
          <a href={`tel:${phone}`} className="text-sm text-blue-600 hover:underline">{phone}</a>
        </div>
      )}
      {email && (
        <div className="flex items-start gap-2">
          <Mail className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); onEmailClick ? onEmailClick(email) : window.open(`/dispatch-console/email?compose=true&to=${encodeURIComponent(email)}`, '_blank') }}
            className="text-sm text-blue-600 hover:underline truncate"
          >{email}</a>
        </div>
      )}
      {address && (
        <div className="flex items-start gap-2">
          <Navigation className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-gray-600 leading-tight">{address}</span>
        </div>
      )}
    </div>
  )
}

// ── Appointment Status Config ──
const APPT_STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  draft:         { label: 'Draft',       color: 'text-gray-500',   bg: 'bg-gray-100' },
  pending:       { label: 'Pending',     color: 'text-amber-600',  bg: 'bg-amber-50' },
  scheduled:     { label: 'Scheduled',   color: 'text-blue-600',   bg: 'bg-blue-50' },
  confirmed:     { label: 'Confirmed',   color: 'text-green-600',  bg: 'bg-green-50' },
  'in-progress': { label: 'In Progress', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  completed:     { label: 'Completed',   color: 'text-green-700',  bg: 'bg-green-100' },
  cancelled:     { label: 'Cancelled',   color: 'text-red-600',    bg: 'bg-red-50' },
  missed:        { label: 'Missed',      color: 'text-red-700',    bg: 'bg-red-100' },
  rejected:      { label: 'Rejected',    color: 'text-orange-700', bg: 'bg-orange-100' },
}

// Fallback transitions (used before backend transitions are loaded)
const APPT_STATUS_TRANSITIONS_FALLBACK: Record<string, ApptStatus[]> = {
  scheduled:     ['confirmed', 'cancelled'],
  confirmed:     ['in-progress', 'cancelled'],
  'in-progress': ['completed', 'missed'],
}

// Singleton cache for transitions loaded from backend
let _transitionsCache: StatusTransitionsMap | null = null
const getTransitionsCache = () => _transitionsCache || APPT_STATUS_TRANSITIONS_FALLBACK
const setTransitionsCache = (t: StatusTransitionsMap) => { _transitionsCache = t }

// Move type labels
const MOVE_TYPE_LABELS: Record<string, string> = {
  'pick-import': 'Pick Import',
  'drop-export': 'Drop Export',
  'pick-empty':  'Pick Empty',
  'drop-empty':  'Drop Empty',
  'drop':        'Delivery',
  'pick':        'Pickup',
  'liveload':    'Live Load',
  'liveunload':  'Live Unload',
}

/**
 * AppointmentDetails —— 这一站那张票的信息：动作、状态、HOLD/费用、SCAC/底盘、备注。
 *
 * 📍 位置：**左栏，地址下面**（不是右栏表单区底下）。
 *
 * 右边那一栏是「要人填的格子」—— 闸口码、PIN、柜位、LFD。票的信息是**读的**，
 * 混在一堆输入框底下既抢不到注意力，又把左栏地址以下那一大片白白空着。
 * 2026-10-01 按用户指的位置搬过来的。
 *
 * 搬过来之后宽度只有三分之一，所以这里**不能再用左右对开的排法** ——
 * 动作/状态一行、状态切换按钮另起一行，两边都允许折行。
 */
const AppointmentDetails: React.FC<{
  appointment: Appointment
  onStatusChange?: (newStatus: ApptStatus) => void
}> = ({ appointment, onStatusChange }) => {
  const statusCfg = APPT_STATUS_CONFIG[appointment.status] || APPT_STATUS_CONFIG.pending
  const transMap = getTransitionsCache()
  const transitions = (transMap[appointment.status as keyof typeof transMap] || []) as ApptStatus[]
  const moveLabel = MOVE_TYPE_LABELS[appointment.moveType] || appointment.moveType

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
      {/* Row 1: Move Type + Status（窄栏里竖着排，按钮另起一行） */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
            {moveLabel}
          </span>
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${statusCfg.color} ${statusCfg.bg}`}>
            {statusCfg.label}
          </span>
          {appointment.rescheduleCount > 0 && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 text-[10px] font-semibold">
              <RefreshCw className="w-2.5 h-2.5" /> ×{appointment.rescheduleCount}
            </span>
          )}
        </div>
        {transitions.length > 0 && onStatusChange && (
          <div className="flex items-center gap-1 flex-wrap">
            {transitions.map(s => {
              const cfg = APPT_STATUS_CONFIG[s] || APPT_STATUS_CONFIG.pending
              return (
                <button
                  key={s}
                  onClick={() => onStatusChange(s)}
                  className={`px-2 py-0.5 rounded text-[10px] font-medium border transition-colors hover:opacity-80 ${cfg.color} ${cfg.bg} border-current/20`}
                >
                  {cfg.label}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Row 2: Hold / Fee indicators */}
      {(appointment.isHold || (appointment.feeDetails && !appointment.feesCleared)) && (
        <div className="flex items-center gap-2 flex-wrap">
          {appointment.isHold && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 text-red-600 text-[10px] font-semibold">
              <ShieldAlert className="w-3 h-3" /> HOLD
              {appointment.holdReasons?.length ? `: ${appointment.holdReasons.join(', ')}` : ''}
            </span>
          )}
          {appointment.feeDetails && !appointment.feesCleared && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-semibold">
              $ Fees Pending
            </span>
          )}
        </div>
      )}

      {/* Row 3: Asset snapshot (SCAC, container, chassis) — only if different from task */}
      {(appointment.scacCode || appointment.chassisNumber) && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
          {appointment.scacCode && <span>SCAC: <span className="font-mono text-gray-700">{appointment.scacCode}</span></span>}
          {appointment.chassisNumber && <span>Chassis: <span className="font-mono text-gray-700">{appointment.chassisNumber}</span></span>}
        </div>
      )}

      {/* Row 4: Notes */}
      {appointment.notes && (
        <p className="text-[11px] text-gray-500 italic">{appointment.notes}</p>
      )}
    </div>
  )
}

// Input styles
const inputClass = "w-full px-2 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"

// True when we hold eModal's own IDs for this appointment, i.e. the edit
// deep link can be built right now.
function isEmodalLinked(appointment?: Appointment): boolean {
  return !!(appointment?.emodalTruckVisitId && appointment?.emodalGateApptConId && appointment?.emodalMtoId);
}

// True when there IS an eModal appointment behind this stop — either
// because we already hold its IDs, or because the stop is one of the
// terminals we have an active eModal profile for (backend flag). The
// second case covers everything typed in by hand, which in production is
// most appointments: the buttons show, and the first click matches the
// appointment on eModal before doing the actual work.
function isEmodalActionable(appointment?: Appointment): boolean {
  if (!appointment) return false;
  return isEmodalLinked(appointment) || appointment.emodalLinkable === true;
}

// B3 — derive whether the Fix Plate button should appear and in which
// state (red=needs fix, green=verified, gray=not linked to eModal yet).
// Returns null when the button should be hidden entirely — which now only
// happens off-eModal (warehouses, PCC, rail ramps), never merely because
// we didn't book the appointment ourselves.
function fixPlateButtonState(appointment?: Appointment): FixPlateState | null {
  if (!isEmodalActionable(appointment)) return null;
  if (!isEmodalLinked(appointment)) return 'unlinked';

  const verified = appointment!.plateVerifiedAt;
  const stamped = appointment!.truckLicensePlate;
  const current = appointment!.currentDriverTruckPlate;

  // Plate hasn't been verified yet, OR current driver/truck differs from
  // what was on the appointment when verified — operator needs to fix.
  if (!verified) return 'needs_fix';
  if (!stamped) return 'needs_fix';
  if (current && current !== stamped) return 'needs_fix';
  return 'verified';
}

// The button itself now lives in components/dispatch/FixPlateButton.tsx so the
// task rows on both dispatch boards render the exact same control — the red
// pulsing button IS the "needs fixing" signal, everywhere it appears.

// Reschedule on eModal — offered on every eModal stop, same rule as Fix
// Plate. Unlinked appointments get matched on eModal on the first click.
function canReschedule(appointment?: Appointment): boolean {
  return isEmodalActionable(appointment);
}

/**
 * 「这张票在码头那边已经没有了」—— 界面唯一的判据。
 *
 * 两个来源，缺一不可：
 *   · `needsRebook` —— 库里明着标的（封闭区解除时只剩不到两小时，migration 191）
 *   · `ticketNumberStale` —— 后端算的：票号里的日子和这行记的时间对不上
 *
 * 🔴 只读第一个是不够的，而且正好在最要命的时候不够：`needs_rebook` 在**改时间
 * 的时候会被自动清掉**，而「票号对不上」恰恰是改时间改出来的。于是同一张票在
 * 派车闸门那里是死的、在这一屏上是绿色的 Scheduled，调度看到的是「有票却派不
 * 出去」，屏幕上一个字都不解释（2026-09-30 线上 WHSU8966050 卡了一整天）。
 *
 * 后端那一半的规则在 `backend/src/utils/apptTicketNumber.js`，派车闸门读的是
 * 同一份 —— 这两边**必须**同生共死，分叉的表现就是绿字骗人。
 */
function isTicketDead(appointment?: Appointment): boolean {
  return !!(appointment?.needsRebook || appointment?.ticketNumberStale);
}

function ticketDeadTitle(appointment?: Appointment): string {
  return (appointment?.ticketNumberStale || appointment?.rebookReason === 'number_date_mismatch')
    ? 'This ticket no longer exists at the terminal — the number and time do not match, usually because the time was changed in Ealoom but not at the terminal. Book a new one.'
    : 'The terminal no longer offers this slot — book a new one.';
}

const RescheduleButton: React.FC<{ onClick: () => void; linked: boolean }> = ({ onClick, linked }) => (
  <button
    type="button"
    onClick={onClick}
    title={linked
      ? 'Reschedule on eModal — pick a new time slot'
      : 'Reschedule on eModal — this appointment was entered by hand, so we will match it on eModal first'}
    aria-label="Reschedule appointment"
    className={
      'inline-flex items-center justify-center h-7 w-7 rounded-md border transition-colors ' +
      (linked
        ? 'border-amber-200 text-amber-600 hover:bg-amber-50 hover:border-amber-300'
        : 'border-gray-300 text-gray-500 hover:bg-gray-50 hover:border-gray-400')
    }
  >
    <CalendarClock className="w-3.5 h-3.5" />
  </button>
);

// Port Node Card
const PortNodeCard: React.FC<{
  node: RouteNode
  index: number
  container: ContainerTask
  onMakeAppointment: () => void
  onRescheduleAppointment?: () => void
  onFixPlate?: (appointmentId: number) => void
  /** Gate Fee — pays this container's terminal gate fee on eModal. */
  onGateFee?: () => void
  onReschedule?: (appointmentId: number) => void
  onContainerChange?: (updates: Partial<ContainerTask>) => void
  appointment?: Appointment
  onAppointmentUpdate?: (appointmentId: number, data: UpdateAppointmentInput) => Promise<void>
  onEmailClick?: (toEmail: string) => void
}> = ({ node, index, container, onMakeAppointment, onRescheduleAppointment, onFixPlate, onGateFee, onReschedule, onContainerChange, appointment, onAppointmentUpdate, onEmailClick }) => {
  const isTBD = node.name === 'TBD' || node.pendingDecision
  const needsAppt = !isTBD && node.appointmentStatus === 'pending'
  // Primary: appointments table. Fallback: route_node (for legacy data without appointment records)
  const apptTime = appointment?.appointmentTime || node.appointmentTime
  // All Day 跟着同一条预约走。这条不带上，「当天不限时段」会被渲染成 00:00，
  // 而且结束时间会留着切换前那个 —— 变成「00:00 - 08:30」这种自相矛盾的显示。
  const apptAllDay = appointment?.allDay ?? node.appointmentAllDay ?? false
  const apptEnd = apptAllDay ? undefined : (appointment?.appointmentEnd || node.appointmentEnd)
  const portContact = container.portContacts?.ssa || container.portContacts?.trapac || container.portContacts?.ets

  // ── 闸口码 / PIN / 货位：**这一站那张票**说了算 ──
  //
  // 🔴 这三个框原来绑的都是 `container.*`，也就是 tasks 上那一族扁平列 —— 一张
  // 任务只有一份，按规矩只代表**第一个码头站**。后果有两种，都在线上见过：
  //   · Live Load 两头各一张票，还柜那一站显示的是**提柜**那张票的闸口码
  //   · 这一站改了码头，显示的还是**旧码头**发的码
  // 2026-09-30 的 WHSU8966050 就是后者：PCC 那一站上写着 531400，那是 TraPac 的。
  // 司机拿着它去闸口，这家码头根本不认。
  //
  // 有票就读票、写票；没有票（老数据、还没约）才退回扁平列 —— 和以前一样。
  const boundToAppt = !!(appointment?.id && onAppointmentUpdate)
  const fieldSource = boundToAppt
    ? {
        gateCode: appointment!.gateCode ?? '',
        pin: appointment!.pin ?? '',
        containerLocation: appointment!.containerLocation ?? '',
      }
    : {
        gateCode: container.gateCode ?? '',
        pin: container.portPin ?? '',
        containerLocation: container.portContainerLocation ?? '',
      }

  // 本地草稿 + 失焦才提交：绑到预约那一支是**当场存库**的，每敲一个字符发一次
  // 请求既浪费也会打架。只在「换了一张票」时重置 —— 跟着值重置会在后台刷新时
  // 把人正在打的字抹掉（见 modal-initialdata-wipe 那一坑）。
  const [terminalFields, setTerminalFields] = React.useState(fieldSource)
  const boundKey = boundToAppt ? `appt:${appointment!.id}` : `task:${container.id}`
  const boundKeyRef = React.useRef(boundKey)
  React.useEffect(() => {
    if (boundKeyRef.current !== boundKey) {
      boundKeyRef.current = boundKey
      setTerminalFields(fieldSource)
    }
    // fieldSource 故意不进依赖：它每次 render 都是新对象，进去就等于跟着值重置。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundKey])

  type TerminalField = keyof typeof terminalFields
  const CONTAINER_KEY: Record<TerminalField, 'gateCode' | 'portPin' | 'portContainerLocation'> = {
    gateCode: 'gateCode', pin: 'portPin', containerLocation: 'portContainerLocation',
  }

  // 没绑到票的照旧「每敲一下就往弹窗的本地状态里写」，Save 按钮那一套不变。
  const changeTerminalField = (key: TerminalField, value: string) => {
    setTerminalFields((prev) => ({ ...prev, [key]: value }))
    if (!boundToAppt) onContainerChange?.({ [CONTAINER_KEY[key]]: value })
  }

  // 绑到票的当场存库，所以等失焦、而且只在真改了的时候发。
  const blurTerminalField = (key: TerminalField) => {
    if (!boundToAppt) return
    const value = terminalFields[key]
    if (value === fieldSource[key]) return
    void onAppointmentUpdate!(appointment!.id, { [key]: value })
  }

  // Format appointment time range for display
  const getApptTimeDisplay = () => {
    if (!apptTime || apptTime === 'Pending') return 'Pending'
    const dateStr = formatDate(apptTime)
    if (apptAllDay) return `${dateStr} All Day`
    const timeRange = formatTimeRange(apptTime, apptEnd)
    return timeRange ? `${dateStr} ${timeRange}` : dateStr
  }

  // Format LFD for the date input. See utils/lfdDate for why this reads the
  // calendar date off the string instead of round-tripping through Date.
  const formatLFDForInput = (lfd?: string): string => lfdDateOnly(lfd) ?? ''

  return (
    <div className={`rounded-xl border-2 overflow-hidden ${isTBD ? 'border-gray-300' : needsAppt ? 'border-amber-300' : 'border-gray-200'}`}>
      {/* Header */}
      <div className={`px-4 py-2.5 flex items-center justify-between ${isTBD ? 'bg-gray-100' : needsAppt ? 'bg-amber-50' : 'bg-blue-50'}`}>
        <div className="flex items-center gap-3">
          <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${isTBD ? 'bg-gray-200 text-gray-600' : 'bg-blue-100 text-blue-600'}`}>
            {index + 1}
          </span>
          <Ship className={`w-4 h-4 ${isTBD ? 'text-gray-400' : 'text-blue-600'}`} />
          <span className={`font-semibold ${isTBD ? 'text-gray-500' : 'text-gray-900'}`}>
            {node.name || container.portShortName || container.portName || 'Port'}
          </span>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
            node.action === 'pick' ? 'bg-blue-200 text-blue-800' : 'bg-orange-200 text-orange-800'
          }`}>
            {node.action === 'pick' ? 'PICKUP' : 'RETURN'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {isTBD ? (
            <span className="flex items-center gap-1 text-xs font-semibold text-gray-500">
              <AlertCircle className="w-3.5 h-3.5" /> Location TBD
            </span>
          ) : needsAppt ? (
            <>
              <span className="flex items-center gap-1 text-xs font-semibold text-amber-600">
                <AlertCircle className="w-3.5 h-3.5" /> Needs Appointment
              </span>
              <Button size="sm" onClick={onMakeAppointment} className="bg-amber-500 hover:bg-amber-600 text-white h-7 text-xs">
                Book
              </Button>
            </>
          ) : (
            <>
              {/*
                🔴 票作废了就**只说 Rebook**，绝不显示绿色的 Scheduled。
                2026-09-20 线上：TIIU5427920 那张还柜票在码头上根本不存在，
                这一屏照旧显示「Scheduled + Mon, Sep 21 07:00」—— 看着完全正常，
                而那趟活是空的。绿字比没有字更危险。
              */}
              {isTicketDead(appointment) ? (
                <span
                  className="flex items-center gap-1 text-xs font-bold text-red-600"
                  title={ticketDeadTitle(appointment)}
                >
                  <AlertTriangle className="w-3.5 h-3.5" /> Rebook
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs font-semibold text-green-600">
                  <CheckCircle className="w-3.5 h-3.5" /> {node.appointmentStatus === 'confirmed' ? 'Confirmed' : 'Scheduled'}
                </span>
              )}
              {(() => {
                const state = fixPlateButtonState(appointment);
                if (!state || !onFixPlate || !appointment?.id) return null;
                return <FixPlateButton state={state} onClick={() => onFixPlate(appointment.id)} />;
              })()}
              {onReschedule && appointment?.id && canReschedule(appointment) && (
                <RescheduleButton
                  onClick={() => onReschedule(appointment.id)}
                  linked={isEmodalLinked(appointment)}
                />
              )}
              {onRescheduleAppointment && (
                <button
                  type="button"
                  onClick={onRescheduleAppointment}
                  title="Edit appointment"
                  aria-label="Edit appointment"
                  className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </>
          )}

          {/* Gate Fee —— 故意放在上面那三个分支之外。
              它原来挂在「已经约好」那一支里，结果正好在最没用的时刻才出现：
              约都约上了，这笔费一般早就付掉了；而费用还欠着、真正需要它的时候，
              这个站点显示的是「Needs Appointment」，按钮根本不在。
              这笔费属于「柜子还压在码头」这件事，不属于「我们有没有订到位」。
              付过之后也继续显示（灰的）—— 第二笔费用是真会有的。 */}
          {onGateFee && node.action === 'pick' && container.importExport === 'import' && (
            <GateFeeButton
              state={container.gateFeePaid ? 'paid' : 'idle'}
              onClick={onGateFee}
            />
          )}
        </div>
      </div>

      {/* Content - Two columns */}
      <div className="flex items-start">
        {/* Left: Contact Info + 这一站那张票的信息 */}
        <div className="w-1/3 p-4 border-r border-gray-100">
          {isTBD ? (
            <p className="text-sm text-gray-400 italic">Location not yet determined</p>
          ) : (
            <ContactInfo
              name={node.fullName || node.name || container.portName}
              contactPerson={node.action === 'return' ? undefined : portContact?.name}
              phone={node.action === 'return' ? undefined : portContact?.phone}
              email={node.action === 'return' ? undefined : portContact?.email}
              address={node.address || container.portAddress}
              onEmailClick={onEmailClick}
            />
          )}
          {/* 票的信息跟着地点走，不跟着输入框走 —— 右栏那一片是要人填的格子，
              票是读的。搬过来之前它压在表单最底下，而左栏地址以下整片空着。 */}
          {appointment && (
            <AppointmentDetails
              appointment={appointment}
              onStatusChange={onAppointmentUpdate ? (s) => onAppointmentUpdate(appointment.id, { status: s }) : undefined}
            />
          )}
        </div>

        {/* Right: Appointment Info (Editable) */}
        <div className="w-2/3 p-4">
          {isTBD ? (
            <div className="text-sm text-gray-500">
              <p className="text-gray-400 italic">Return location needs to be determined</p>
              <p className="mt-2 text-xs text-gray-400">
                Appointment details will be available after location is set.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
              {/* Appointment Time - Display only (spans full width) */}
              <div className="col-span-2">
                <label className="text-xs font-medium text-gray-500 uppercase block mb-1">Appointment Time</label>
                {(!apptTime || apptTime === 'Pending') ? (
                  <span className="text-sm text-amber-600 font-medium">Pending — use Book above to schedule</span>
                ) : (
                  <span className="text-sm font-medium text-gray-900">{getApptTimeDisplay()}</span>
                )}
              </div>

              {/* Gate Code —— 这一站那张票的，不是整张任务的（见上面 fieldSource） */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase block mb-1">Gate Code</label>
                <input
                  type="text"
                  value={terminalFields.gateCode}
                  onChange={(e) => changeTerminalField('gateCode', e.target.value)}
                  onBlur={() => blurTerminalField('gateCode')}
                  className={`${inputClass} font-mono`}
                  placeholder="Enter gate code"
                />
              </div>

              {/* Port PIN */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase block mb-1">PIN</label>
                <input
                  type="text"
                  value={terminalFields.pin}
                  onChange={(e) => changeTerminalField('pin', e.target.value)}
                  onBlur={() => blurTerminalField('pin')}
                  className={`${inputClass} font-mono`}
                  placeholder="Terminal PIN"
                />
              </div>

              {/* Port Container Location */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase block mb-1">Container Location</label>
                <input
                  type="text"
                  value={terminalFields.containerLocation}
                  onChange={(e) => changeTerminalField('containerLocation', e.target.value)}
                  onBlur={() => blurTerminalField('containerLocation')}
                  className={`${inputClass} font-mono`}
                  placeholder="e.g. H1002"
                />
              </div>

              {/* Type - Dropdown */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase block mb-1">Type</label>
                {/* Unknown is the starting state, not a blank. The old default
                    was 'Wheeled', which made an unanswered field look like an
                    answer — and since the ETS parser was reading a column that
                    does not exist, that fallback was what every ETS container
                    displayed. Wheeled/Decked is a claim, and only the terminal
                    (or a dispatcher who called them) gets to make it.

                    Unknown is stored as NULL — the column only accepts the two
                    real states — so picking it back is how you retract a wrong
                    claim and let the next check fill it in again. */}
                <Select
                  value={container.type || 'Unknown'}
                  onChange={(v) => onContainerChange?.({ type: v === 'Unknown' ? null : (v as 'Decked' | 'Wheeled') })}
                  style={{ width: '100%' }}
                >
                  <Select.Option value="Unknown">Unknown</Select.Option>
                  <Select.Option value="Wheeled">Wheeled</Select.Option>
                  <Select.Option value="Decked">Decked</Select.Option>
                </Select>
              </div>

              {/* LFD - Date Picker */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase block mb-1">LFD (Last Free Day)</label>
                <input
                  type="date"
                  value={formatLFDForInput(container.lfd)}
                  onChange={(e) => {
                    const dateValue = e.target.value
                    if (dateValue) {
                      const lfdDateTime = `${dateValue}T23:59:59`
                      onContainerChange?.({ lfd: lfdDateTime })
                    }
                  }}
                  className={inputClass}
                />
              </div>

              {/* Location - Container yard position within terminal */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase block mb-1">Location</label>
                <input
                  type="text"
                  value={container.location || ''}
                  onChange={(e) => onContainerChange?.({ location: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. Bay 12, Row C"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Warehouse Node Card
const WarehouseNodeCard: React.FC<{
  node: RouteNode
  index: number
  container: ContainerTask
  onMakeAppointment: () => void
  onRescheduleAppointment?: () => void
  onReschedule?: (appointmentId: number) => void
  onContainerChange?: (updates: Partial<ContainerTask>) => void
  appointment?: Appointment
  onAppointmentUpdate?: (appointmentId: number, data: UpdateAppointmentInput) => Promise<void>
  onEmailClick?: (toEmail: string) => void
}> = ({ node, index, container, onMakeAppointment, onRescheduleAppointment, onReschedule, onContainerChange, appointment, onAppointmentUpdate, onEmailClick }) => {
  const isTBD = node.name === 'TBD' || node.pendingDecision
  // appointmentRequired: undefined/true means required (backwards compat)
  const apptRequired = node.appointmentRequired !== false
  const needsAppt = !isTBD && apptRequired && node.appointmentStatus === 'pending'
  // Primary: appointments table. Fallback: route_node (for legacy data without appointment records)
  const apptTime = appointment?.appointmentTime || node.appointmentTime
  const apptAllDay = appointment?.allDay ?? node.appointmentAllDay ?? false   // 见港口卡
  const apptEnd = apptAllDay ? undefined : (appointment?.appointmentEnd || node.appointmentEnd)
  const hasAppt = apptTime && apptTime !== 'Pending'

  // Format appointment time range for display
  const getApptTimeDisplay = () => {
    if (!apptTime || apptTime === 'Pending') return 'Pending'
    const dateStr = formatDate(apptTime)
    if (apptAllDay) return `${dateStr} All Day`
    const timeRange = formatTimeRange(apptTime, apptEnd)
    return timeRange ? `${dateStr} ${timeRange}` : dateStr
  }

  return (
    <div className={`rounded-xl border-2 overflow-hidden ${isTBD ? 'border-gray-300' : needsAppt ? 'border-amber-300' : 'border-gray-200'}`}>
      {/* Header */}
      <div className={`px-4 py-2.5 flex items-center justify-between ${isTBD ? 'bg-gray-100' : needsAppt ? 'bg-amber-50' : !apptRequired ? 'bg-gray-50' : 'bg-orange-50'}`}>
        <div className="flex items-center gap-3">
          <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${isTBD ? 'bg-gray-200 text-gray-600' : 'bg-orange-100 text-orange-600'}`}>
            {index + 1}
          </span>
          <Warehouse className={`w-4 h-4 ${isTBD ? 'text-gray-400' : 'text-orange-600'}`} />
          <span className={`font-semibold ${isTBD ? 'text-gray-500' : 'text-gray-900'}`}>
            {node.name || container.warehouseShortName || container.warehouseName || container.warehouse || 'Warehouse'}
          </span>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
            node.action === 'pick' ? 'bg-blue-200 text-blue-800' : 'bg-green-200 text-green-800'
          }`}>
            {node.action === 'pick' ? 'PICKUP' : 'DELIVERY'}
          </span>
          {node.loadingStatus && (
            <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
              {node.loadingStatus === 'loading' ? 'Loading' : 'Unloading'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isTBD ? (
            <span className="flex items-center gap-1 text-xs font-semibold text-gray-500">
              <AlertCircle className="w-3.5 h-3.5" /> Location TBD
            </span>
          ) : !apptRequired && !hasAppt ? (
            <>
              <span className="flex items-center gap-1 text-xs font-medium text-gray-500">
                <CheckCircle className="w-3.5 h-3.5 text-gray-400" /> No Appt Required
              </span>
              <Button size="sm" variant="outline" onClick={onMakeAppointment} className="h-7 text-xs border-gray-300 text-gray-500 hover:bg-gray-100">
                Book
              </Button>
            </>
          ) : needsAppt ? (
            <>
              <span className="flex items-center gap-1 text-xs font-semibold text-amber-600">
                <AlertCircle className="w-3.5 h-3.5" /> Needs Appointment
              </span>
              <Button size="sm" onClick={onMakeAppointment} className="bg-amber-500 hover:bg-amber-600 text-white h-7 text-xs">
                Book
              </Button>
            </>
          ) : (
            <>
              {/*
                🔴 票作废了就**只说 Rebook**，绝不显示绿色的 Scheduled。
                2026-09-20 线上：TIIU5427920 那张还柜票在码头上根本不存在，
                这一屏照旧显示「Scheduled + Mon, Sep 21 07:00」—— 看着完全正常，
                而那趟活是空的。绿字比没有字更危险。
              */}
              {isTicketDead(appointment) ? (
                <span
                  className="flex items-center gap-1 text-xs font-bold text-red-600"
                  title={ticketDeadTitle(appointment)}
                >
                  <AlertTriangle className="w-3.5 h-3.5" /> Rebook
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs font-semibold text-green-600">
                  <CheckCircle className="w-3.5 h-3.5" /> {node.appointmentStatus === 'confirmed' ? 'Confirmed' : 'Scheduled'}
                </span>
              )}
              {onReschedule && appointment?.id && canReschedule(appointment) && (
                <RescheduleButton
                  onClick={() => onReschedule(appointment.id)}
                  linked={isEmodalLinked(appointment)}
                />
              )}
              {onRescheduleAppointment && (
                <button
                  type="button"
                  onClick={onRescheduleAppointment}
                  title="Edit appointment"
                  aria-label="Edit appointment"
                  className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Content - Two columns */}
      <div className="flex items-start">
        {/* Left: Contact Info + 这一站那张预约的信息（和码头站同一个排法） */}
        <div className="w-1/3 p-4 border-r border-gray-100">
          <ContactInfo
            name={container.warehouseName || container.warehouse || node.name}
            contactPerson={container.warehouseContact?.name}
            phone={container.warehouseContact?.phone || container.warehousePhone}
            email={container.warehouseContact?.email}
            address={container.warehouseAddress}
            onEmailClick={onEmailClick}
          />
          {appointment && (
            <AppointmentDetails
              appointment={appointment}
              onStatusChange={onAppointmentUpdate ? (s) => onAppointmentUpdate(appointment.id, { status: s }) : undefined}
            />
          )}
        </div>

        {/* Right: Appointment Info (Editable) */}
        <div className="w-2/3 p-4">
          {!apptRequired && !hasAppt ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-500">No appointment needed for this warehouse.</p>
              <p className="text-xs text-gray-400">You can still book an optional appointment using the Book button above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
              {/* Appointment Time - Display only (spans full width) */}
              <div className="col-span-2">
                <label className="text-xs font-medium text-gray-500 uppercase block mb-1">Appointment Time</label>
                {(!apptTime || apptTime === 'Pending') ? (
                  <span className="text-sm text-amber-600 font-medium">Pending — use Book above to schedule</span>
                ) : (
                  <span className="text-sm font-medium text-gray-900">{getApptTimeDisplay()}</span>
                )}
              </div>

              {/* Appointment Number - Editable */}
              <div className="col-span-2">
                <label className="text-xs font-medium text-gray-500 uppercase block mb-1">Appointment Number</label>
                <input
                  type="text"
                  value={container.appointmentNumber || ''}
                  onChange={(e) => onContainerChange?.({ appointmentNumber: e.target.value })}
                  className={`${inputClass} font-mono`}
                  placeholder="Enter appointment number"
                />
              </div>

              {/* Warehouse PIN */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase block mb-1">PIN</label>
                <input
                  type="text"
                  value={container.warehousePin || ''}
                  onChange={(e) => onContainerChange?.({ warehousePin: e.target.value })}
                  className={`${inputClass} font-mono`}
                  placeholder="Warehouse PIN"
                />
              </div>

              {/* Warehouse Container Location */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase block mb-1">Dock / Location</label>
                <input
                  type="text"
                  value={container.warehouseContainerLocation || ''}
                  onChange={(e) => onContainerChange?.({ warehouseContainerLocation: e.target.value })}
                  className={`${inputClass} font-mono`}
                  placeholder="e.g. Dock 5, A-12"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Yard Node Card
const YardNodeCard: React.FC<{
  node: RouteNode
  index: number
  container: ContainerTask
  onEmailClick?: (toEmail: string) => void
}> = ({ node, index, container, onEmailClick }) => {
  const isTBD = node.name === 'TBD' || node.pendingDecision

  return (
    <div className={`rounded-xl border-2 overflow-hidden ${isTBD ? 'border-gray-300' : 'border-gray-100'}`}>
      {/* Header */}
      <div className={`px-4 py-2.5 flex items-center justify-between ${isTBD ? 'bg-gray-100' : 'bg-purple-50'}`}>
        <div className="flex items-center gap-3">
          <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${isTBD ? 'bg-gray-200 text-gray-600' : 'bg-purple-100 text-purple-600'}`}>
            {index + 1}
          </span>
          <MapPin className={`w-4 h-4 ${isTBD ? 'text-gray-400' : 'text-purple-600'}`} />
          <span className={`font-semibold ${isTBD ? 'text-gray-500' : 'text-gray-900'}`}>
            {node.name || container.yardShortName || container.yardName || 'Yard'}
          </span>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
            node.action === 'pick' ? 'bg-blue-200 text-blue-800' : 'bg-orange-200 text-orange-800'
          }`}>
            {node.action === 'pick' ? 'PICKUP' : 'RETURN'}
          </span>
        </div>
        {isTBD ? (
          <span className="flex items-center gap-1 text-xs font-semibold text-gray-500">
            <AlertCircle className="w-3.5 h-3.5" /> Location TBD
          </span>
        ) : (
          <span className="text-xs text-gray-400">No appointment needed</span>
        )}
      </div>

      {/* Content - Two columns like warehouse */}
      <div className="flex items-start">
        {/* Left: Contact Info */}
        <div className="w-1/3 p-4 border-r border-gray-100">
          <ContactInfo
            name={container.yardName || node.name}
            contactPerson={container.yardContact?.name}
            phone={container.yardContact?.phone}
            email={container.yardContact?.email}
            address={container.yardAddress || node.address}
            onEmailClick={onEmailClick}
          />
        </div>

        {/* Right: Info (No appointment needed for yard) */}
        <div className="w-2/3 p-4">
          <div className="space-y-2">
            <div className="text-sm text-gray-500">
              <span className="text-gray-400">Status:</span>
              <span className="ml-2 text-gray-700">No appointment required</span>
            </div>
            {container.yardCity && container.yardState && (
              <div className="text-sm">
                <span className="text-gray-400">City:</span>
                <span className="ml-2 text-gray-700">{container.yardCity}, {container.yardState}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// CBP Node Card
const PCCNodeCard: React.FC<{
  node: RouteNode
  index: number
  container: ContainerTask
  onEmailClick?: (toEmail: string) => void
}> = ({ node, index, container, onEmailClick }) => {
  return (
    <div className="rounded-xl border-2 overflow-hidden border-amber-200">
      {/* Header */}
      <div className="px-4 py-2.5 flex items-center justify-between bg-amber-50">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold bg-amber-100 text-amber-600">
            {index + 1}
          </span>
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span className="font-semibold text-gray-900">
            {node.name || container.pccLocationName || 'CBP'}
          </span>
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-200 text-amber-800">
            {node.action === 'pick' ? 'PICKUP' : 'INSPECTION'}
          </span>
        </div>
        <span className="text-xs text-gray-400">No appointment needed</span>
      </div>

      {/* Content - Two columns */}
      <div className="flex items-start">
        {/* Left: Contact Info */}
        <div className="w-1/3 p-4 border-r border-gray-100">
          <ContactInfo
            name={container.pccLocationName || node.name}
            contactPerson={null}
            phone={null}
            email={null}
            address={container.pccLocationAddress || node.address}
            onEmailClick={onEmailClick}
          />
        </div>

        {/* Right: Info */}
        <div className="w-2/3 p-4">
          <div className="space-y-2">
            <div className="text-sm text-gray-500">
              <span className="text-gray-400">Status:</span>
              <span className="ml-2 text-amber-600 font-medium">CBP Inspection</span>
            </div>
            {container.pccHoldReason && (
              <div className="text-sm">
                <span className="text-gray-400">Reason:</span>
                <span className="ml-2 text-gray-700">{container.pccHoldReason}</span>
              </div>
            )}
            {container.pccLocationCity && (
              <div className="text-sm">
                <span className="text-gray-400">City:</span>
                <span className="ml-2 text-gray-700">{container.pccLocationCity}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper: derive expected move_type from a route node + task context
const deriveNodeMoveType = (node: RouteNode, container: ContainerTask): string | null => {
  if (node.type === 'port') {
    const ie = container.importExport
    if (node.action === 'pick') return ie === 'export' ? 'pick-empty' : 'pick-import'
    if (node.action === 'return') return ie === 'import' ? 'drop-empty' : 'drop-export'
    return null
  }
  if (node.type === 'warehouse') {
    const isLive = container.taskSubtype === 'live-load'
    if (isLive) return container.importExport === 'import' ? 'liveunload' : 'liveload'
    if (node.action === 'pick') return 'pick'
    return 'drop'
  }
  return null
}

export const RouteAppointmentsTab: React.FC<RouteAppointmentsTabProps> = ({
  container,
  onMakePortAppointment,
  onMakeWarehouseAppointment,
  onReschedulePortAppointment,
  onRescheduleWarehouseAppointment,
  onFixPlate,
  onGateFee,
  onReschedule,
  onReroute,
  onStoreToYard,
  onCancelStoreToYard,
  onReleaseNextLeg,
  onSendToPCC,
  onContainerChange,
  driver,
  allContainerTasks,
  appointments,
  onAppointmentUpdate,
}) => {
  const [showRouteActions, setShowRouteActions] = React.useState(false)
  const routeActionsRef = React.useRef<HTMLDivElement>(null)

  // Load transitions from backend once (cached in module-level singleton)
  React.useEffect(() => {
    if (_transitionsCache) return
    appointmentApi.getTransitions().then(setTransitionsCache).catch(() => { /* use fallback */ })
  }, [])

  // Close dropdown on outside click
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (routeActionsRef.current && !routeActionsRef.current.contains(e.target as Node)) {
        setShowRouteActions(false)
      }
    }
    if (showRouteActions) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showRouteActions])
  // Build location-specific email compose URL
  const buildEmailClick = (locationType: 'port' | 'warehouse' | 'yard' | 'pcc', node: RouteNode, appt?: Appointment) => (toEmail: string) => {
    const c = container
    const taskType = c.taskType?.replace(/-/g, ' ').replace(/\b\w/g, (ch: string) => ch.toUpperCase()) || ''
    const action = node.action === 'pick' ? 'Pickup' : node.action === 'return' ? 'Return' : node.action === 'delivery' ? 'Delivery' : node.action || ''
    const locationName = node.fullName || node.name || ''

    const lines: string[] = []

    if (locationType === 'port') {
      const subject = `[${c.orderDisplayId || ''}] ${c.containerNumber || ''} - Port ${action}`.trim()
      const portApptNum = appt?.appointmentNumber || node.appointmentNumber
      if (portApptNum) lines.push(`Appointment #: ${portApptNum}`)
      if (c.containerNumber) lines.push(`Container #: ${c.containerNumber}`)
      if (c.importExport === 'export') {
        if (c.bookingNumber) lines.push(`Booking #: ${c.bookingNumber}`)
      } else {
        if (c.bol) lines.push(`BOL: ${c.bol}`)
      }
      if (c.shippingLine) lines.push(`Shipping Line: ${c.shippingLine}`)
      if (c.containerSize) lines.push(`Container Size: ${c.containerSize}`)
      const portApptTime = appt?.appointmentTime || node.appointmentTime
      // 这段是发给码头的邮件正文。All Day 写成 "00:00" 不只是界面难看，
      // 是把错的时间告诉对方。
      const portAllDay = appt?.allDay ?? node.appointmentAllDay ?? false
      const portApptEnd = portAllDay ? undefined : (appt?.appointmentEnd || node.appointmentEnd)
      if (portApptTime && portApptTime !== 'Pending') {
        const dateStr = formatDate(portApptTime)
        const timeRange = portAllDay ? 'All Day' : formatTimeRange(portApptTime, portApptEnd)
        lines.push(`Appointment Time: ${dateStr}${timeRange ? ' ' + timeRange : ''}`)
      }
      if (c.gateCode) lines.push(`Gate Code: ${c.gateCode}`)
      if (c.pin) lines.push(`PIN: ${c.pin}`)
      if (c.lfd) lines.push(`LFD: ${formatLfdMedium(c.lfd, { year: true })}`)

      const body = `\n\n--- Port ${action} Info ---\n${lines.join('\n')}\n---`
      const params = new URLSearchParams({ compose: 'true', to: toEmail, subject, body })
      window.open(`/dispatch-console/email?${params.toString()}`, '_blank')

    } else if (locationType === 'warehouse') {
      const whName = c.warehouseName || c.warehouse || locationName
      const subject = `[${c.orderDisplayId || ''}] ${c.containerNumber || ''} - WH ${action}`.trim()
      const whApptNum = appt?.appointmentNumber || node.appointmentNumber
      if (whApptNum) lines.push(`Appointment #: ${whApptNum}`)
      if (c.containerNumber) lines.push(`Container #: ${c.containerNumber}`)
      if (c.importExport === 'export') {
        if (c.bookingNumber) lines.push(`Booking #: ${c.bookingNumber}`)
      } else {
        if (c.bol) lines.push(`BOL: ${c.bol}`)
      }
      const whApptTime = appt?.appointmentTime || node.appointmentTime
      const whAllDay = appt?.allDay ?? node.appointmentAllDay ?? false   // 见港口分支
      const whApptEnd = whAllDay ? undefined : (appt?.appointmentEnd || node.appointmentEnd)
      if (whApptTime && whApptTime !== 'Pending') {
        const dateStr = formatDate(whApptTime)
        const timeRange = whAllDay ? 'All Day' : formatTimeRange(whApptTime, whApptEnd)
        lines.push(`Appointment Time: ${dateStr}${timeRange ? ' ' + timeRange : ''}`)
      }
      if (whName) lines.push(`Warehouse: ${whName}`)
      if (c.containerSize) lines.push(`Container Size: ${c.containerSize}`)

      const body = `\n\n--- Warehouse ${action} Info ---\n${lines.join('\n')}\n---`
      const params = new URLSearchParams({ compose: 'true', to: toEmail, subject, body })
      window.open(`/dispatch-console/email?${params.toString()}`, '_blank')

    } else {
      // Yard / PCC — basic info
      const label = locationType === 'pcc' ? 'PCC' : 'Yard'
      const subject = `[${c.orderDisplayId || ''}] ${c.containerNumber || ''} - ${label} ${action}`.trim()
      if (c.containerNumber) lines.push(`Container #: ${c.containerNumber}`)
      if (c.importExport === 'export') {
        if (c.bookingNumber) lines.push(`Booking #: ${c.bookingNumber}`)
      } else {
        if (c.bol) lines.push(`BOL: ${c.bol}`)
      }
      if (locationName) lines.push(`${label}: ${locationName}`)
      if (c.orderDisplayId) lines.push(`Order #: ${c.orderDisplayId}`)
      if (c.taskId) lines.push(`Task: ${c.taskId} (${taskType})`)

      const body = `\n\n--- ${label} Info ---\n${lines.join('\n')}\n---`
      const params = new URLSearchParams({ compose: 'true', to: toEmail, subject, body })
      window.open(`/dispatch-console/email?${params.toString()}`, '_blank')
    }
  }

  // Generate route nodes — memoized on container reference
  // Container is a state object that gets a new reference on meaningful changes
  const routeNodes = React.useMemo(() => generateRouteNodes(container), [container])
  const isLiveload = isLiveload3NodeTask(container)
  const daysUntilLFD = getDaysUntilLFD(container.lfd)
  const lfdUrgency = getLFDUrgency(daysUntilLFD)
  const pendingCount = routeNodes.filter(n => n.appointmentStatus === 'pending' && n.appointmentType !== 'none' && n.appointmentRequired !== false).length

  const hasDriverData = driver && driver.id && driver.id !== 'unassigned' && driver.name !== 'Unassigned'
  // Disable route actions for tasks in terminal statuses
  const terminalStatuses = ['completed', 'cancelled', 'uncompleted']
  const taskStatus = (container.status || '').toLowerCase()
  const isTerminalTask = terminalStatuses.includes(taskStatus)
  const hasPendingYardStorage = !!container.pendingYardStorage
  const hasRouteActions = !isTerminalTask && (onReroute || onStoreToYard || onCancelStoreToYard || onReleaseNextLeg || onSendToPCC)

  return (
    <div className="flex gap-6">
      {/* Left: Main route content */}
      <div className="flex-1 min-w-0 space-y-5">
        {/* Header row: title + badges */}
        <div className="flex items-center justify-between">
          <h5 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
            <div className="w-1 h-4 bg-blue-500 rounded-full"></div>
            Route & Appointments
          </h5>
          <div className="flex items-center gap-2">
            {isLiveload && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                <Truck className="w-3 h-3" /> Liveload
              </span>
            )}
            {pendingCount > 0 && (
              <span className="flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 rounded text-xs font-semibold">
                <AlertCircle className="w-3.5 h-3.5" /> {pendingCount} Appt Needed
              </span>
            )}
            {lfdUrgency && lfdUrgency !== 'normal' && (
              <span className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold ${
                lfdUrgency === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
              }`}>
                <AlertTriangle className="w-3.5 h-3.5" /> LFD {daysUntilLFD! <= 0 ? 'PAST' : `${daysUntilLFD}d`}
              </span>
            )}
          </div>
        </div>

        {/* Route Stepper — full width, nodes evenly distributed */}
        <div className="flex items-center w-full py-2.5 px-3 bg-gray-50 border border-gray-200 rounded-xl">
          {routeNodes.map((node, i) => {
            const Icon = LOCATION_ICONS[node.type] || MapPin
            const colors = COLORS[node.type] || COLORS.shipper
            const isCompleted = node.status === 'completed'
            const isInProgress = node.status === 'in-progress'
            const isLast = i === routeNodes.length - 1
            const actionLabel = node.action === 'pick' ? 'Pickup' : node.action === 'delivery' ? 'Delivery' : node.action === 'return' ? 'Return' : node.action
            return (
              <React.Fragment key={i}>
                {/* Node — flex:1 to distribute evenly */}
                <div className="flex items-center gap-2.5 justify-center" style={{ flex: '1 1 0', minWidth: 0 }}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm border-2 ${
                    isCompleted
                      ? 'bg-green-500 border-green-200'
                      : isInProgress
                        ? `${colors.bg} border-blue-300 ring-2 ring-blue-100`
                        : `${colors.bg} border-white`
                  }`}>
                    {isCompleted ? <CheckCircle className="w-4 h-4 text-white" /> : <Icon className="w-4 h-4 text-white" />}
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className={`text-[13px] font-semibold leading-tight truncate ${
                      isCompleted ? 'text-green-700' : isInProgress ? 'text-blue-700' : 'text-gray-800'
                    }`} title={node.name}>
                      {node.name}
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-wide ${
                      isCompleted ? 'text-green-500' : 'text-gray-400'
                    }`}>
                      {actionLabel}
                    </span>
                  </div>
                </div>
                {/* Connector — stretches to fill gap */}
                {!isLast && (
                  <div className="flex items-center flex-shrink-0 w-12">
                    <div className={`flex-1 border-t-2 border-dashed ${isCompleted ? 'border-green-400' : 'border-gray-300'}`} />
                    <div className={`w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[7px] ${
                      isCompleted ? 'border-l-green-400' : 'border-l-gray-300'
                    }`} />
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>

        {/* Stop Cards */}
        <div className="space-y-3">
          {routeNodes.map((node, i) => {
            // Match appointment from the appointments table by move_type —
            // plus, for port stops, the terminal it was actually booked at.
            //
            // move_type alone says "the empty return leg", not "the empty
            // return leg AT THIS TERMINAL". Change a stop's terminal and the
            // old terminal's ticket keeps matching: the card then reads
            // "SSA · Scheduled 14:00" over a TraPac gate code, and the eModal
            // buttons on it open TraPac (2026-09-23, WHSU8211578). A stop that
            // honestly says "needs appointment" is the lesser evil — and the
            // failures centre carries the "cancel the old ticket" line.
            //
            // Only filters when both sides are known: legacy rows have no
            // facilityId, and a placeholder stop has no id.
            const moveType = deriveNodeMoveType(node, container)
            const sameFacility = (a: Appointment) => {
              if (node.type !== 'port') return true
              if (a.facilityId == null || node.id == null) return true
              return Number(a.facilityId) === Number(node.id)
            }
            const matchedAppt = moveType && appointments
              ? appointments.find(a =>
                  a.moveType === moveType
                  && !['cancelled', 'rejected'].includes(a.status)
                  && sameFacility(a)
                )
              : undefined

            if (node.type === 'port') {
              return <PortNodeCard key={i} node={node} index={i} container={container} onMakeAppointment={() => onMakePortAppointment(i)} onRescheduleAppointment={onReschedulePortAppointment ? () => onReschedulePortAppointment(i) : undefined} onFixPlate={onFixPlate} onGateFee={onGateFee} onReschedule={onReschedule} onContainerChange={onContainerChange} appointment={matchedAppt} onAppointmentUpdate={onAppointmentUpdate} onEmailClick={buildEmailClick('port', node, matchedAppt)} />
            }
            if (node.type === 'warehouse') {
              return <WarehouseNodeCard key={i} node={node} index={i} container={container} onMakeAppointment={onMakeWarehouseAppointment} onRescheduleAppointment={onRescheduleWarehouseAppointment} onReschedule={onReschedule} onContainerChange={onContainerChange} appointment={matchedAppt} onAppointmentUpdate={onAppointmentUpdate} onEmailClick={buildEmailClick('warehouse', node, matchedAppt)} />
            }
            if (node.type === 'pcc') {
              return <PCCNodeCard key={i} node={node} index={i} container={container} onEmailClick={buildEmailClick('pcc', node)} />
            }
            return <YardNodeCard key={i} node={node} index={i} container={container} onEmailClick={buildEmailClick('yard', node)} />
          })}
        </div>

        {/* Bottom: Task Info + Route Actions Dropdown */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>Type: <span className="font-medium text-gray-700">{container.taskType?.replace('-', ' ')}</span></span>
            <span>Subtype: <span className={`font-medium ${container.taskSubtype === 'live-load' ? 'text-purple-600' : 'text-gray-700'}`}>
              {container.taskSubtype === 'live-load' ? 'Live Load' : 'Drop'}
            </span></span>
            <span>I/E: <span className={`font-medium ${container.importExport === 'export' ? 'text-orange-600' : 'text-blue-600'}`}>
              {container.importExport === 'export' ? 'Export' : 'Import'}
            </span></span>
            {container.lfd && (
              <span>LFD: <span className={`font-medium ${
                lfdUrgency === 'critical' ? 'text-red-600' : lfdUrgency === 'warning' ? 'text-amber-600' : 'text-gray-700'
              }`}>{formatLfdMedium(container.lfd)}</span></span>
            )}
          </div>

          {/* Route Actions Dropdown */}
          {hasRouteActions && (
            <div className="relative" ref={routeActionsRef}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowRouteActions(!showRouteActions)}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 h-8 text-xs gap-1"
              >
                Route Actions
                <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showRouteActions ? 'rotate-90' : ''}`} />
              </Button>
              {showRouteActions && (
                <div className="absolute right-0 bottom-full mb-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
                  {onReroute && (
                    <button
                      onClick={() => { setShowRouteActions(false); onReroute() }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    >
                      Reroute
                    </button>
                  )}
                  {onStoreToYard && !hasPendingYardStorage && (
                    <button
                      onClick={() => { setShowRouteActions(false); onStoreToYard() }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-cyan-50 hover:text-cyan-700 transition-colors"
                    >
                      Store to Yard
                    </button>
                  )}
                  {onReleaseNextLeg && hasPendingYardStorage && (
                    <button
                      onClick={() => { setShowRouteActions(false); onReleaseNextLeg() }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-cyan-50 hover:text-cyan-700 transition-colors"
                      title="Create the yard-to-destination leg now so it can be assigned and planned for a later day. It only becomes runnable once this leg is completed."
                    >
                      Release Next Leg Now
                    </button>
                  )}
                  {onCancelStoreToYard && hasPendingYardStorage && (
                    <button
                      onClick={() => { setShowRouteActions(false); onCancelStoreToYard() }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                    >
                      Cancel Store to Yard
                    </button>
                  )}
                  {onSendToPCC && (
                    <button
                      onClick={() => { setShowRouteActions(false); onSendToPCC() }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                    >
                      Send to CBP
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right sidebar: Driver Card + Driver Notes */}
      <div className="w-[340px] flex-shrink-0 space-y-4">
        <h5 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
          <div className="w-1 h-4 bg-green-500 rounded-full"></div>
          Driver
        </h5>

        {hasDriverData ? (
          <DriverCard
            driver={driver!}
            variant="detailed"
            containerTasks={Array.isArray(allContainerTasks) ? allContainerTasks : []}
            config={{ defaultTaskListOpen: false }}
            messageTaskId={container?.id ? Number(container.id) : undefined}
          />
        ) : (
          <div className="bg-gray-50 rounded-xl p-6 text-center">
            <div className="w-12 h-12 bg-gray-200 rounded-full mx-auto mb-3 flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <p className="text-gray-600 font-medium text-sm">No Driver Assigned</p>
            <p className="text-xs text-gray-400 mt-1">Assign a driver to see info</p>
          </div>
        )}

        {/* Driver Notes */}
        <div>
          <h5 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3 flex items-center gap-2">
            <div className="w-1 h-4 bg-amber-500 rounded-full"></div>
            Driver Notes
          </h5>
          <div className="bg-gray-50 rounded-xl p-3">
            <textarea
              value={container.dispatcherToDriverInstructions || ''}
              onChange={(e) => onContainerChange?.({ dispatcherToDriverInstructions: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
              rows={4}
              placeholder="Add instructions for the driver..."
            />
          </div>
        </div>
      </div>
    </div>
  )
}

